import { Link } from "react-router-dom";
import { Mail, FlaskConical, BookOpen, Shield, FileText, AlertTriangle } from "lucide-react";
import { useLang } from "@/context/LangContext";

const LOGO_URL = "/api/uploads/brand/logo.png";
// Hoist year out of render — eslint react-hooks/purity flags Date() inside components.
const CURRENT_YEAR = new Date().getFullYear();

export const Footer = () => {
  const { t } = useLang();

  const footerLinks = {
    shop: [
      { label: "Home", href: "/" },
      { label: "Shop", href: "/shop" },
      { label: "About", href: "/about" },
      { label: "Contact", href: "/contact" },
      { label: "Verify Batch", href: "/verify" },
    ],
    research: [
      { label: t("footer_research_batch"), href: "/verify" },
      { label: t("footer_research_coa"), href: "/verify" },
    ],
    account: [
      { label: t("footer_account_login"), href: "/login" },
      { label: t("footer_account_register"), href: "/register" },
      { label: t("footer_account_dashboard"), href: "/dashboard" },
      { label: t("footer_account_orders"), href: "/dashboard/orders" },
    ],
    legal: [
      { label: t("footer_terms"), href: "/terms" },
      { label: t("footer_privacy"), href: "/privacy" },
      { label: t("footer_refund"), href: "/refund-policy" },
      { label: "FDA Disclaimer", href: "/fda-disclaimer" },
    ],
  };

  return (
    <footer className="relative bg-[#06070A] border-t border-white/5" data-testid="main-footer">
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img src={LOGO_URL} alt="Hexxium Research" className="h-12 w-auto" />
            </Link>
            <div className="flex gap-2 mt-2">
              <input
                type="email"
                placeholder={t("footer_newsletter_placeholder")}
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-[#00AEEF] focus:outline-none transition-colors"
                data-testid="newsletter-input"
              />
              <button
                className="px-4 py-2.5 bg-[#00AEEF] hover:bg-[#1AA8FF] text-white text-sm font-medium rounded-lg transition-colors"
                data-testid="newsletter-submit"
              >
                <Mail className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Shop Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-[#00AEEF]" />
              Navigate
            </h4>
            <ul className="space-y-3">
              {footerLinks.shop.map((link) => (
                <li key={link.href + link.label}>
                  <Link to={link.href} className="text-slate-400 hover:text-white text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Research Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-[#00AEEF]" />
              {t("footer_research_title")}
            </h4>
            <ul className="space-y-3">
              {footerLinks.research.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-slate-400 hover:text-white text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Account Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#00AEEF]" />
              {t("footer_account_title")}
            </h4>
            <ul className="space-y-3">
              {footerLinks.account.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-slate-400 hover:text-white text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="border-t border-white/5 pt-8 mb-8 space-y-4">
          <div className="glass rounded-xl p-6">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h5 className="text-amber-400 font-medium mb-1">Research Use Only</h5>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {t("footer_disclaimer_text")}
                </p>
                <p className="text-slate-400 text-sm leading-relaxed mt-2">
                  Hexxium Research is not a compounding pharmacy or chemical compounding facility as defined under 503A of the Federal Food, Drug, and Cosmetic Act.
                </p>
                <Link to="/fda-disclaimer" className="text-[#00AEEF] hover:underline text-xs mt-2 inline-block">
                  View Full FDA Disclaimer &rarr;
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-white/5">
          <p className="text-slate-500 text-sm">
            &copy; {CURRENT_YEAR} {t("footer_copyright")}
          </p>
          <div className="flex items-center gap-6">
            {footerLinks.legal.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className="text-slate-500 hover:text-white text-sm transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <button
              onClick={() => window.dispatchEvent(new Event("hexxium:cookie-settings"))}
              className="text-slate-500 hover:text-white text-sm transition-colors"
              data-testid="cookie-settings-link"
            >
              Cookie Settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
